import { create } from "zustand";
import { persist } from "zustand/middleware";

export type MiProfileDateMode = "preset" | "custom";

/** Landing default on /mi-contenido when no custom range is set. */
export const DEFAULT_MI_CONTENIDO_PRESET_DAYS = 7 as const;

type ProfileFiltersPersist = {
  dateMode: MiProfileDateMode;
  presetDays: 7 | 30;
  customStartDate: string | null;
  customEndDate: string | null;
  selectedPodcastId: string | null;
};

export interface ProfileState {
  dateMode: MiProfileDateMode;
  presetDays: 7 | 30;
  customStartDate: string | null;
  customEndDate: string | null;
  selectedPodcastId: string | null;
  setPresetDays: (days: 7 | 30) => void;
  setCustomRange: (start: string, end: string) => void;
  setSelectedPodcastId: (id: string | null) => void;
  resetToDefaults: () => void;
}

export const useProfileStore = create<ProfileState>()(
  persist(
    (set) => ({
      dateMode: "preset",
      presetDays: DEFAULT_MI_CONTENIDO_PRESET_DAYS,
      customStartDate: null,
      customEndDate: null,
      selectedPodcastId: null,
      setPresetDays: (presetDays) =>
        set({ dateMode: "preset", presetDays }),
      setCustomRange: (customStartDate, customEndDate) =>
        set({
          dateMode: "custom",
          customStartDate,
          customEndDate,
        }),
      setSelectedPodcastId: (selectedPodcastId) => set({ selectedPodcastId }),
      resetToDefaults: () =>
        set({
          dateMode: "preset",
          presetDays: DEFAULT_MI_CONTENIDO_PRESET_DAYS,
          customStartDate: null,
          customEndDate: null,
          selectedPodcastId: null,
        }),
    }),
    {
      name: "profile-filters",
      version: 2,
      migrate: (persistedState, version): ProfileFiltersPersist => {
        const p = (persistedState ?? {}) as Partial<ProfileFiltersPersist> & {
          preset?: number;
        };
        const next: ProfileFiltersPersist = {
          dateMode: p.dateMode === "custom" ? "custom" : "preset",
          presetDays: p.presetDays === 7 || p.presetDays === 30 ? p.presetDays : DEFAULT_MI_CONTENIDO_PRESET_DAYS,
          customStartDate: p.customStartDate ?? null,
          customEndDate: p.customEndDate ?? null,
          selectedPodcastId: p.selectedPodcastId ?? null,
        };
        if (version < 2 && next.dateMode !== "custom") {
          next.presetDays = DEFAULT_MI_CONTENIDO_PRESET_DAYS;
        }
        return next;
      },
      merge: (persistedState, currentState) => {
        const p = persistedState as Record<string, unknown> | undefined;
        if (!p) return currentState as ProfileState;
        if (
          typeof p.preset === "number" &&
          p.dateMode === undefined
        ) {
          const presetDays: 7 | 30 = p.preset === 7 ? 7 : 30;
          return {
            ...(currentState as ProfileState),
            dateMode: "preset",
            presetDays,
            customStartDate: null,
            customEndDate: null,
            selectedPodcastId: (p.selectedPodcastId as string | null) ?? null,
          };
        }
        return { ...(currentState as ProfileState), ...p } as ProfileState;
      },
      partialize: (state) => ({
        dateMode: state.dateMode,
        presetDays: state.presetDays,
        customStartDate: state.customStartDate,
        customEndDate: state.customEndDate,
        selectedPodcastId: state.selectedPodcastId,
      }),
    },
  ),
);
